import { Autocomplete, Button, Stack, TextField } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import { emailValidator } from "@entrilia/shared/utilities/validators";
import type { ClientInfo, OrganizationUserInfo } from "../../../api/clientUsersTypes";
import { api } from "../../../api/operationsApi";
import { useExecutionState } from "../../../hooks/useExecutionState";
import ExecutionStatusAlert from "../../common/ExecutionStatusAlert";
import FieldHelp from "../../common/FieldHelp";
import LoadingFailedAlert from "../../common/LoadingFailedAlert";
import { buildRenameUserMessage } from "./renameUserMessage";

const renameUser = withErrorHandling(api.clientUsers.renameUser);

interface Props {
  organization: ClientInfo;
}

const RenameUserForm = ({ organization }: Props) => {
  const { executionState, setExecutionState } = useExecutionState();
  const [selectedUser, setSelectedUser] = useState<OrganizationUserInfo | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emailValidationResult, setEmailValidationResult] = useState<ValidationResult>();

  const getUsers = useCallback(
    () => api.clientUsers.getOrganizationUsers({ clientCode: organization.clientCode }),
    [organization.clientCode]
  );
  const [usersResponse, usersFetchError] = useFetch(getUsers);

  if (usersFetchError) {
    logError(usersFetchError, "[RenameUserForm] getOrganizationUsers");
    return <LoadingFailedAlert apiError={usersFetchError} />;
  }

  if (usersResponse === undefined) {
    return <InlineLoader />;
  }

  const users = usersResponse.users;
  const displayName = firstName.trim() && lastName.trim() ? `${lastName.trim()}, ${firstName.trim()}` : "";
  const canSubmit = !!selectedUser && !!firstName.trim() && !!lastName.trim() && !!newEmail.trim();

  const handleRename = async () => {
    if (!selectedUser) {
      return;
    }

    const sanitizedEmail = sanitizeEmail(newEmail);
    const validationResult = emailValidator(sanitizedEmail);
    setEmailValidationResult(validationResult);
    if (!validationResult.isValid) {
      return;
    }

    setExecutionState({ status: "in-progress" });

    const [resp, error] = await renameUser({
      clientCode: organization.clientCode,
      userId: selectedUser.userId,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      newEmail: sanitizedEmail,
      displayName,
    });

    if (error) {
      logError(error, "[RenameUserForm] renameUser");
      setExecutionState({ status: "error", error });
      return;
    }

    setExecutionState(buildRenameUserMessage(resp, organization.name));
  };

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "100%" }}>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <Autocomplete
          fullWidth
          options={users}
          value={selectedUser}
          onChange={(_, value) => setSelectedUser(value)}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          isOptionEqualToValue={(option, value) => option.userId === value.userId}
          renderInput={(params) => <TextField {...params} label="User (search by name or email)" />}
        />
        <FieldHelp>Type a name or email to find the user to rename.</FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField fullWidth label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        <FieldHelp>The user&apos;s new first name.</FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField fullWidth label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        <FieldHelp>The user&apos;s new last name.</FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField
          fullWidth
          label="New email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          error={emailValidationResult?.isValid === false}
          helperText={emailValidationResult?.error || (displayName ? `Display name will be "${displayName}"` : "")}
        />
        <FieldHelp>The user&apos;s new email. Usually the same company domain with a new part before the @.</FieldHelp>
      </Stack>
      <Button
        variant="contained"
        color="primary"
        onClick={handleRename}
        loading={executionState.status === "in-progress"}
        disabled={!canSubmit}
      >
        Rename
      </Button>
      <ExecutionStatusAlert executionState={executionState} setExecutionState={setExecutionState} />
    </Stack>
  );
};

export default RenameUserForm;
